// Tasks Page - View and manage operational tasks
import React, { useState, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import AdminHeader from '../../../shared/components/AdminHeader';
import AdminDataTable from '../../../shared/components/AdminDataTable';
import TabBar from '../../../shared/components/TabBar';

type PlatformRole = 'admin' | 'operator';

interface TasksProps {
  brand: BrandConfig;
  Icons: IconsType;
  onNavigate: (module: string, subSection?: string) => void;
  platformRole?: PlatformRole;
}

interface TaskItem {
  id: string;
  type: string;
  status: string;
  priority: string;
  title: string;
  description: string;
  orderId?: string;
  dueAt?: string;
  createdAt: string;
  assigneeId?: string;
  assignee?: {
    displayName: string;
  };
  completedAt?: string;
  [key: string]: unknown;
}

// SLA Definitions:
// - Standard: Within 3 business days
// - Priority: Next business day (24 hours from submission)
// - Urgent: Same day (end of day 23:59)
// - On-Demand: Specific time requested by customer

// Helper to get end of today (23:59)
const getEndOfDay = () => {
  const eod = new Date();
  eod.setHours(23, 59, 0, 0);
  return eod;
};

// Mock tasks data
const mockTasks: TaskItem[] = [
  {
    id: '440e8400-e29b-41d4-a716-446655440001',
    type: 'follow_up',
    status: 'pending',
    priority: 'urgent', // End of day 23:59
    title: 'Follow up on rejected filing',
    description: 'Contact client regarding rejected filing for Brown v. Taylor.',
    orderId: '550e8400-e29b-41d4-a716-446655440004',
    dueAt: getEndOfDay().toISOString(), // Due at 23:59 today
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '440e8400-e29b-41d4-a716-446655440002',
    type: 'qa_review',
    status: 'pending',
    priority: 'priority', // 24 hours from submission
    title: 'QA review for Smith v. Jones filing',
    description: 'Perform quality assurance review before final submission.',
    orderId: '550e8400-e29b-41d4-a716-446655440001',
    dueAt: new Date(Date.now() - 4 * 60 * 60 * 1000 + 24 * 60 * 60 * 1000).toISOString(), // 24h from submission = 20h remaining
    createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '440e8400-e29b-41d4-a716-446655440003',
    type: 'exception_handling',
    status: 'in_progress',
    priority: 'priority', // 24 hours from submission - getting close
    title: 'Handle court rejection notice',
    description: 'Review rejection reasons and prepare correction.',
    assigneeId: '660e8400-e29b-41d4-a716-446655440002',
    assignee: { displayName: 'Operator Two' },
    dueAt: new Date(Date.now() - 21 * 60 * 60 * 1000 + 24 * 60 * 60 * 1000).toISOString(), // 24h from submission = 3h remaining
    createdAt: new Date(Date.now() - 21 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '440e8400-e29b-41d4-a716-446655440004',
    type: 'document_review',
    status: 'in_progress',
    priority: 'standard', // Within 3 business days
    title: 'Review uploaded documents',
    description: 'Verify all uploaded documents meet court requirements.',
    assigneeId: '660e8400-e29b-41d4-a716-446655440001',
    assignee: { displayName: 'Operator One' },
    dueAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days from now
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '440e8400-e29b-41d4-a716-446655440005',
    type: 'client_outreach',
    status: 'completed',
    priority: 'standard', // Within 3 days - completed on day 1
    title: 'Contact client about fee schedule',
    description: 'Called client to explain court fee structure.',
    assigneeId: '660e8400-e29b-41d4-a716-446655440001',
    assignee: { displayName: 'Operator One' },
    completedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: '440e8400-e29b-41d4-a716-446655440006',
    type: 'other',
    status: 'pending',
    priority: 'on-demand', // Customer-specified deadline - ~1 week out
    title: 'Update client contact info',
    description: 'Client requested phone number update by next week.',
    dueAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week from now
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
  },
];

const typeLabels = {
  follow_up: 'Follow Up',
  qa_review: 'QA Review',
  exception_handling: 'Exception',
  document_review: 'Doc Review',
  client_outreach: 'Client Outreach',
  other: 'Other',
};

const statusLabels = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const statusColors = {
  pending: '#F59E0B',
  in_progress: '#3B82F6',
  completed: '#10B981',
  cancelled: '#6B7280',
};

// Priority colors aligned with business SLAs
const priorityColors = {
  standard: '#6B7280',   // Gray - default (3 business days)
  priority: '#F59E0B',   // Amber - expedited (next business day)
  urgent: '#EF4444',     // Red - rush (same day)
  'on-demand': '#8B5CF6', // Purple - custom deadline
};

// Priority labels for display
const priorityLabels = {
  standard: 'Standard',
  priority: 'Priority',
  urgent: 'Urgent',
  'on-demand': 'On-Demand',
};

const Tasks: React.FC<TasksProps> = ({ brand, Icons, onNavigate, platformRole: _platformRole }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [tasks, setTasks] = useState<TaskItem[]>(mockTasks);
  const [_showCreateModal, setShowCreateModal] = useState(false);
  void _showCreateModal; // Will be used for create modal
  void _platformRole; // Will be used for role-based features
  void onNavigate; // Will be used for navigation

  // Filter tabs
  const filterTabs = [
    { id: 'all', label: 'All Tasks', count: tasks.filter(t => t.status !== 'cancelled').length },
    { id: 'pending', label: 'Pending', count: tasks.filter(t => t.status === 'pending').length },
    { id: 'in_progress', label: 'In Progress', count: tasks.filter(t => t.status === 'in_progress').length },
    { id: 'unassigned', label: 'Unassigned', count: tasks.filter(t => !t.assigneeId && t.status === 'pending').length },
    { id: 'completed', label: 'Completed', count: tasks.filter(t => t.status === 'completed').length },
  ];

  // Apply filters
  const filteredTasks = useMemo(() => {
    let items = [...tasks].filter(t => t.status !== 'cancelled');

    switch (activeFilter) {
      case 'pending':
        items = items.filter(t => t.status === 'pending');
        break;
      case 'in_progress':
        items = items.filter(t => t.status === 'in_progress');
        break;
      case 'unassigned':
        items = items.filter(t => !t.assigneeId && t.status === 'pending');
        break;
      case 'completed':
        items = items.filter(t => t.status === 'completed');
        break;
      default:
        break;
    }

    // Sort by priority order: urgent first, then on-demand, priority, standard
    const priorityOrder = { urgent: 0, 'on-demand': 1, priority: 2, standard: 3 };
    items.sort((a, b) => {
      const aOrder = priorityOrder[a.priority] ?? 99;
      const bOrder = priorityOrder[b.priority] ?? 99;
      return aOrder - bOrder;
    });

    return items;
  }, [tasks, activeFilter]);

  const handleClaim = async (taskId) => {
    setTasks(prev => prev.map(t =>
      t.id === taskId
        ? { ...t, status: 'in_progress', assigneeId: 'me', assignee: { displayName: 'You' } }
        : t
    ));
  };

  const handleComplete = async (taskId) => {
    setTasks(prev => prev.map(t =>
      t.id === taskId
        ? { ...t, status: 'completed', completedAt: new Date().toISOString() }
        : t
    ));
  };

  const formatTimeRemaining = (dateString) => {
    if (!dateString) return '-';
    const diff = new Date(dateString).getTime() - Date.now(); // eslint-disable-line react-hooks/purity -- time-remaining must reflect current time at render
    if (diff < 0) return 'Overdue';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    // Show days (rounded down) when > 24 hours
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const isOverdue = (task) => {
    return task.dueAt && new Date(task.dueAt).getTime() < Date.now() && task.status !== 'completed'; // eslint-disable-line react-hooks/purity -- overdue check must reflect current time at render
  };

  // Table columns
  const columns = [
    {
      key: 'priority',
      label: 'Priority',
      render: (value) => (
        <span
          className="px-2 py-0.5 rounded-sm text-xs font-medium"
          style={{
            backgroundColor: `${priorityColors[value] || '#6B7280'}20`,
            color: priorityColors[value] || '#6B7280',
          }}
        >
          {priorityLabels[value] || value}
        </span>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (value) => (
        <span className="text-sm" style={{ color: brand.colors.textLight }}>
          {typeLabels[value] || value}
        </span>
      ),
    },
    {
      key: 'title',
      label: 'Task',
      render: (value, row) => (
        <div>
          <div className="font-medium" style={{ color: brand.colors.text }}>{value}</div>
          {row.description && (
            <div className="text-xs truncate max-w-xs" style={{ color: brand.colors.textLight }}>
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span
          className="px-2 py-0.5 rounded-sm text-xs font-medium"
          style={{
            backgroundColor: `${statusColors[value] || '#6B7280'}20`,
            color: statusColors[value] || '#6B7280',
          }}
        >
          {statusLabels[value] || value}
        </span>
      ),
    },
    {
      key: 'assignee',
      label: 'Assignee',
      render: (value) => (
        <span className="text-sm" style={{ color: value ? brand.colors.text : brand.colors.textLight }}>
          {value?.displayName || 'Unassigned'}
        </span>
      ),
    },
    {
      key: 'dueAt',
      label: 'Due',
      render: (value, row) => (
        <span
          className="text-sm"
          style={{
            color: isOverdue(row) ? brand.colors.error : brand.colors.text,
            fontWeight: isOverdue(row) ? '600' : '400',
          }}
        >
          {formatTimeRemaining(value)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (_, row) => {
        if (row.status === 'completed') return null;

        return (
          <div className="flex items-center gap-2">
            {!row.assigneeId && (
              <button
                onClick={() => handleClaim(row.id)}
                className="px-2 py-1 text-xs rounded-sm transition-colors"
                style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              >
                Claim
              </button>
            )}
            {row.assigneeId && row.status !== 'completed' && (
              <button
                onClick={() => handleComplete(row.id)}
                className="px-2 py-1 text-xs rounded-sm transition-colors"
                style={{ backgroundColor: brand.colors.success, color: 'white' }}
              >
                Complete
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: brand.colors.bgLight }}>
      <AdminHeader
        title="Tasks"
        subtitle="Internal operational tasks"
        brand={brand}
        Icons={Icons}
        rightContent={
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            <Icons.Plus className="w-4 h-4" />
            New Task
          </button>
        }
      />

      {/* Summary Cards */}
      <div className="px-6 py-4 border-b" style={{ backgroundColor: 'white', borderColor: brand.colors.border }}>
        <div className="flex gap-4 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
            <span className="text-2xl font-bold" style={{ color: '#D97706' }}>
              {tasks.filter(t => t.status === 'pending').length}
            </span>
            <span className="text-sm" style={{ color: '#92400E' }}>Pending</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#DBEAFE' }}>
            <span className="text-2xl font-bold" style={{ color: '#2563EB' }}>
              {tasks.filter(t => t.status === 'in_progress').length}
            </span>
            <span className="text-sm" style={{ color: '#1E40AF' }}>In Progress</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
            <span className="text-2xl font-bold" style={{ color: '#EF4444' }}>
              {tasks.filter(t => isOverdue(t)).length}
            </span>
            <span className="text-sm" style={{ color: '#B91C1C' }}>Overdue</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: brand.colors.bgLight }}>
            <span className="text-2xl font-bold" style={{ color: brand.colors.textLight }}>
              {tasks.filter(t => !t.assigneeId && t.status === 'pending').length}
            </span>
            <span className="text-sm" style={{ color: brand.colors.textLight }}>Unassigned</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <TabBar
        tabs={filterTabs}
        activeTab={activeFilter}
        onTabChange={setActiveFilter}
        brand={brand}
      />

      {/* Tasks Table */}
      <div className="flex-1 overflow-auto p-6">
        {filteredTasks.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-12 rounded-xl"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <Icons.Check className="w-12 h-12 mb-3" style={{ color: brand.colors.success }} />
            <h3 className="text-lg font-medium mb-1" style={{ color: brand.colors.text }}>
              No tasks found
            </h3>
            <p className="text-sm" style={{ color: brand.colors.textLight }}>
              {activeFilter === 'all' ? 'Create a new task to get started' : 'No tasks match this filter'}
            </p>
          </div>
        ) : (
          <div
            className="rounded-xl overflow-hidden"
            style={{ backgroundColor: 'white', border: `1px solid ${brand.colors.border}` }}
          >
            <AdminDataTable
              columns={columns}
              data={filteredTasks}
              brand={brand}
              Icons={Icons}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Tasks;
